<template>
    <modals-generic
    showFooter
    buttonFullWidth
    applySecondLayer
    preventDetailsModalClose
    helperTextTitle
    :title="t('join.createAccount.step4.generalInfo.addCorporateInterestModal.title')"
    :buttonsFooterData="buttonsFooterData"
    :triggerClose="triggerClose"
    :scrollValue="scrollValue"
    @hideMe="emit('hideMe')"
    @clearScrollToValue="scrollValue = null">
        <div class="group-wrapper">
            <div class="description">{{t('join.createAccount.step4.generalInfo.addCorporateInterestModal.description')}}</div>
            <!-- Interest Name -->
            <input-generic
            id="interestsName"
            :params="interestsNameParams"
            :inputValue="corporateInterestForm.interestsName"
            @input="[corporateInterestForm.interestsName = $event, clearFieldError('interestsNameParams')]"
            @onBlur="interestsNameValidator"/>
            <!-- Interest NiF -->
            <input-generic
            id="interestsNif"
            :params="interestsNifParams"
            :inputValue="corporateInterestForm.interestsNif"
            @input="[corporateInterestForm.interestsNif = $event, clearFieldError('interestsNifParams')]"/>
            <!-- Interest Country -->
            <dropdown-generic
            id="interestsCountry"
            :params="interestsCountryParams"
            :options="getCountries"
            :selectedOption="corporateInterestForm.interestsCountry"
            @clicked="[corporateInterestForm.interestsCountry = $event, clearFieldError('interestsCountryParams')]"/>
            <!-- Interests Percentage Of Capital -->
            <input-generic
            id="interestsPercentageOfCapital"
            :params="interestsPercentageOfCapitalParams"
            :inputValue="corporateInterestForm.interestsPercentageOfCapital"
            @input="[corporateInterestForm.interestsPercentageOfCapital = $event, clearFieldError('interestsPercentageOfCapitalParams')]"/>
        </div>
    </modals-generic>
</template>

<script setup>
import ModalsGeneric from '@/components/generic/modals/side/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'

/* Vue & Helpers */
import { ref } from 'vue'
import { percentageValidator } from '@/assets/scripts/validators'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'updateCorporateInterestInfo'])

/** Props
@index {Number} - when editing a corporate interest, we will receive an index to send back when saving info
@corporateInterestInfo {Object} - Object with corporate interest info
*/
const props = defineProps({
    index: Number,
    corporateInterestInfo: Object
})

const { getCountries } = mapGetters(moduleMapper.JOIN_CREATE_ACCOUNT)

/* Data */
const triggerClose = ref(false)
const scrollValue = ref(0)

const corporateInterestForm = ref({
    interestsName: props.corporateInterestInfo ? props.corporateInterestInfo.interestsName : '',
    interestsNif: props.corporateInterestInfo ? props.corporateInterestInfo.interestsNif : '',
    interestsCountry: props.corporateInterestInfo ? props.corporateInterestInfo.interestsCountry : '',
    interestsPercentageOfCapital: props.corporateInterestInfo ? props.corporateInterestInfo.interestsPercentageOfCapital : ''
})

/*************************************************
 *                  Interests Name               *
 *************************************************/
const interestsNameParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsName'),
    placeholder: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsNamePlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return value.toUpperCase() }
    }
})

/*************************************************
 *                   Interests NIF               *
 *************************************************/
const interestsNifParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsNif'),
    placeholder: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsNifPlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return value.toUpperCase() }
    }
})

/*************************************************
 *                Interests Country              *
 *************************************************/
const interestsCountryParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsCountry'),
    placeholder: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsCountryPlaceholder'),
    withSearch: true,
	mandatory: true,
    errorState: false,
    errorMessage: null
})

/*************************************************
 *         Interests Percentage Of Capital       *
 *************************************************/
const interestsPercentageOfCapitalParams = ref({
    labelText: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsPercentageOfCapital'),
    placeholder: t('join.createAccount.step4.generalInfo.addCorporateInterestModal.interestsPercentageOfCapitalPlaceholder'),
    mandatory: true,
    applyNumbersIntInputMode: true,
    ourMask: {
        validatorFn: value => { return percentageValidator(value, 100) }
    }
})

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
    interestsNameParams: interestsNameParams.value,
    interestsNifParams: interestsNifParams.value,
    interestsCountryParams: interestsCountryParams.value,
    interestsPercentageOfCapitalParams: interestsPercentageOfCapitalParams.value
})

/*
* We have all mandatory fields here,
* but we comment the ones that depend on some option
*/
const mandatoryList = ref([
    'interestsName',
    'interestsNif',
    'interestsCountry',
    'interestsPercentageOfCapital'
])

const showMandatoryErrorsHandler = () => {
   /* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(corporateInterestForm.value, mandatoryList.value)
    if (mandatoryFieldsEmptyList.length) {
        /* 90px is for sideModal header curtain */
        scrollValue.value = getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]) - 90

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    } else {
        emit('updateCorporateInterestInfo', { info: corporateInterestForm.value, index: props.index })
        triggerClose.value = true
    }
}

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/
const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

/**********************************************************************
*                                                                     *
*                                Footer                               *
*                                                                     *
***********************************************************************/
const buttonsFooterData = [{
	label: t('join.createAccount.step4.saveBtn'),
	buttonType: 'primary',
	callbackFunction: showMandatoryErrorsHandler
}]
</script>

<style lang="scss" scoped>
.description {
    @include H6-Regular;
    color: var(--secondary-dark-2);
    line-height: 18px;
}
</style>
